import { TASK_PRIORITY_LABELS, type TaskPriority } from "@sebas/core";
import { format, isPast, isToday } from "date-fns";
import { CalendarClock, FileText } from "lucide-react";
import { cn } from "@/lib/cn";
import type { Task } from "@/lib/tasks";

/**
 * Priority uses the tile palette, not HakiUI's semantic Badge colours.
 *
 * A soft Badge renders its colour as *text* on a 13% tint, and no single hue
 * clears 4.5:1 on both grounds: HakiUI's `danger` red measured 3.63:1 on the
 * dark ground, and coral measured 2.56:1 on cream. The tile rule already solves
 * this — saturated background, fixed dark ink — and it is what the due-date pill
 * beside it already uses, so this is the more consistent choice as well.
 */
const PRIORITY_TILE: Record<TaskPriority, string> = {
  high: "bg-tile-coral",
  medium: "bg-tile-amber",
  low: "bg-tile-sand",
};

/** Due-date wording that says what matters: overdue, today, or the date. */
function dueLabel(dueAt: string) {
  const d = new Date(dueAt);
  if (isToday(d)) return { text: "Due today", urgent: true };
  if (isPast(d)) return { text: `Overdue · ${format(d, "d MMM")}`, urgent: true };
  return { text: format(d, "d MMM"), urgent: false };
}

export function TaskCard({
  task,
  onOpen,
  draggable = false,
  onDragStart,
  onDragEnd,
  dragging = false,
}: {
  task: Task;
  onOpen: () => void;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  dragging?: boolean;
}) {
  const due = task.dueAt ? dueLabel(task.dueAt) : null;
  const done = task.status === "done";

  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className={cn(
        "flex w-full flex-col gap-2 rounded-2xl border border-line bg-raised p-3 text-left",
        "transition-shadow hover:shadow-card",
        dragging && "opacity-40",
        draggable && "cursor-grab active:cursor-grabbing",
      )}
    >
      <span className={cn("text-sm font-medium text-ink", done && "line-through opacity-60")}>
        {task.title}
      </span>

      {task.description ? (
        <span className="line-clamp-2 text-xs text-ink-muted">{task.description}</span>
      ) : null}

      <span className="flex flex-wrap items-center gap-1.5">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-medium text-tile-ink",
            PRIORITY_TILE[task.priority],
          )}
        >
          {TASK_PRIORITY_LABELS[task.priority]}
        </span>

        {due ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem]",
              // Overdue stops being urgent once the task is done.
              due.urgent && !done ? "bg-tile-coral text-tile-ink" : "bg-sunken text-ink-muted",
            )}
          >
            <CalendarClock className="h-3 w-3" />
            {due.text}
          </span>
        ) : null}

        {task.notes.length > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-sunken px-2 py-0.5 text-[0.65rem] text-ink-muted">
            <FileText className="h-3 w-3" />
            {task.notes.length}
          </span>
        ) : null}
      </span>
    </button>
  );
}
